import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { ArrowLeft, Flame, Trophy, Users as UsersIcon, Clock, Crown, UserPlus, Check, X as XIcon, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import TierBadge from "../components/TierBadge";
import { trackEvent, EVENTS } from "../lib/analytics";

const fmtHours = (seconds) => {
  const h = Math.floor(seconds / 3600);
  if (h >= 1000) return `${(h / 1000).toFixed(1)}k`;
  return h;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

export default function PublicProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const { effectivePremium } = useAccess();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/by-username/${username}`);
      setProfile(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("User not found");
        navigate("/community");
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't load profile");
      }
    } finally { setLoading(false); }
  }, [username, navigate]);

  useEffect(() => { load(); }, [load]);

  const sendRequest = async () => {
    if (!profile) return;
    setActionLoading(true);
    try {
      await api.post(`/friends/request/by-id/${profile.user_id}`);
      trackEvent(EVENTS.FRIEND_ADDED, { method: "public_profile" });
      setProfile((p) => ({ ...p, friend_status: "request_sent" }));
      toast.success("Friend request sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    } finally { setActionLoading(false); }
  };

  const removeConnection = async (label) => {
    if (!profile) return;
    setActionLoading(true);
    try {
      await api.delete(`/friends/${profile.user_id}`);
      setProfile((p) => ({ ...p, friend_status: "none" }));
      toast.success(label);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally { setActionLoading(false); }
  };

  if (loading || !profile) {
    return <div className="p-8 text-center text-sm text-zinc-500" data-testid="profile-loading">Loading profile…</div>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="public-profile-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-emerald-600"
        data-testid="profile-back-btn"
      >
        <ArrowLeft size={14}/> Back
      </button>

      <section className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900 sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          {profile.picture ? (
            <img src={profile.picture} alt={profile.name} className="h-20 w-20 rounded-2xl object-cover sm:h-24 sm:w-24"/>
          ) : (
            <div className="grid h-20 w-20 place-items-center rounded-2xl bg-emerald-500 text-2xl font-semibold text-white sm:h-24 sm:w-24">
              {(profile.name?.[0] || "?").toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl font-bold sm:text-3xl">{profile.name || profile.username}</h1>
              <TierBadge tier={profile.tier} />
            </div>
            <div className="mt-0.5 text-sm text-zinc-500">@{profile.username}</div>
            {profile.bio && (
              <p className="mt-3 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">{profile.bio}</p>
            )}
            <div className="mt-3 text-xs text-zinc-400">Joined {fmtDate(profile.created_at)}</div>
          </div>
        </div>

        {profile.friend_status !== "self" && effectivePremium && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {profile.friend_status === "none" && (
              <Button onClick={sendRequest} disabled={actionLoading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="profile-add-btn">
                <UserPlus size={14} className="mr-1.5"/> Send friend request
              </Button>
            )}
            {profile.friend_status === "request_sent" && (
              <Button variant="outline" onClick={() => removeConnection("Request cancelled")} disabled={actionLoading} data-testid="profile-cancel-btn">
                <XIcon size={14} className="mr-1.5"/> Cancel request
              </Button>
            )}
            {profile.friend_status === "request_received" && (
              <span className="text-sm text-emerald-600">They sent you a request — check the Community tab to accept.</span>
            )}
            {profile.friend_status === "friends" && (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                  <Check size={12}/> Friends
                </span>
                <Button variant="outline" size="sm" onClick={() => navigate("/messages")} data-testid="profile-message-btn">
                  <MessageCircle size={14} className="mr-1.5"/> Message
                </Button>
                <Button variant="ghost" size="sm" onClick={() => removeConnection("Friend removed")} disabled={actionLoading} data-testid="profile-remove-btn">
                  Remove
                </Button>
              </>
            )}
          </div>
        )}
        {profile.friend_status !== "self" && !effectivePremium && (
          <div className="mt-5 rounded-xl border border-amber-300/30 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
            <Crown size={12} className="mr-1 -mt-0.5 inline"/> Friends & messaging are premium features.
          </div>
        )}
      </section>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="profile-stats">
        <StatTile icon={Flame} label="Streak" value={`${profile.streak_days}d`} accent="text-amber-500"/>
        <StatTile icon={Clock} label="Hours" value={`${fmtHours(profile.total_study_seconds)}h`} accent="text-emerald-500"/>
        <StatTile
          icon={Trophy}
          label="Rank"
          value={profile.leaderboard_rank ? `#${profile.leaderboard_rank}` : "—"}
          accent="text-amber-500"
          sub="weekly"
        />
        <StatTile icon={UsersIcon} label="Friends" value={profile.friends_count} accent="text-blue-500"/>
      </section>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className={`inline-flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800 ${accent}`}>
        <Icon size={16}/>
      </div>
      <div className="mt-2.5 font-heading text-xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-zinc-500">{label}{sub && <span className="ml-1 text-zinc-400">· {sub}</span>}</div>
    </div>
  );
}
